import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Package, Shirt, TrendingUp, Users, AlertTriangle, X, ShoppingBag } from 'lucide-react';
import CollectionCard from '../components/collections/CollectionCard';
import { analyticsAPI, adminAPI, ordersAPI } from '../services/api';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat('en-IN').format(Number(value) || 0);

const Dashboard = () => {
  const { collections, products, refreshData } = useApp();
  const [overview, setOverview] = useState(null);
  const [totalOrders, setTotalOrders] = useState(null);
  const [orderStatusCounts, setOrderStatusCounts] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetResult, setResetResult] = useState(null);

  const handleResetData = async () => {
    if (resetConfirmText.trim().toUpperCase() !== 'RESET') return;
    setResetting(true);
    setResetError('');
    setResetResult(null);
    try {
      const res = await adminAPI.resetStoreData();
      setResetResult(res);
      try {
        await analyticsAPI.getOverview(30).then((r) => r?.success && setOverview(r.kpis));
        await ordersAPI.getAdminOrders({ page: 1, limit: 1 }).then((r) => {
          if (r?.success) {
            setTotalOrders(r.total ?? 0);
            setOrderStatusCounts(r.status_counts || null);
          }
        });
        if (refreshData) await refreshData();
      } catch (e) {
        /* non-fatal refresh */
      }
    } catch (err) {
      setResetError(err.message || 'Failed to reset data');
    } finally {
      setResetting(false);
    }
  };

  const closeResetModal = () => {
    if (resetting) return;
    setShowResetModal(false);
    setResetConfirmText('');
    setResetError('');
    setResetResult(null);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingStats(true);
        const [overviewRes, ordersRes] = await Promise.all([
          analyticsAPI.getOverview(30),
          ordersAPI.getAdminOrders({ page: 1, limit: 1 }),
        ]);
        if (active && overviewRes?.success) {
          setOverview(overviewRes.kpis);
        }
        if (active && ordersRes?.success) {
          setTotalOrders(ordersRes.total ?? 0);
          setOrderStatusCounts(ordersRes.status_counts || null);
        }
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        if (active) setLoadingStats(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const stats = [
    {
      name: 'Total Orders',
      value: loadingStats ? '—' : formatNumber(totalOrders),
      icon: ShoppingBag,
      color: 'bg-blue-500',
      change: null,
      subtitle: loadingStats ? null : (totalOrders === 1 ? 'order placed' : 'orders placed'),
    },
    {
      name: 'Total Products',
      value: formatNumber(products.length),
      icon: Shirt,
      color: 'bg-green-500',
      change: null,
      subtitle: `${products.length === 1 ? 'product' : 'products'} live`,
    },
    {
      name: 'Monthly Revenue',
      value: loadingStats ? '—' : formatCurrency(overview?.revenue),
      icon: TrendingUp,
      color: 'bg-purple-500',
      change: loadingStats ? null : overview?.revenue_change,
    },
    {
      name: 'Active Customers',
      value: loadingStats ? '—' : formatNumber(overview?.customers),
      icon: Users,
      color: 'bg-orange-500',
      change: loadingStats ? null : overview?.customers_change,
    }
  ];

  const renderChange = (change) => {
    if (change === null || change === undefined) return null;
    const positive = Number(change) >= 0;
    return (
      <p className={`text-sm mt-1 ${positive ? 'text-green-600' : 'text-red-600'}`}>
        {positive ? '+' : ''}{change}% from last month
      </p>
    );
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome back, Admin!</h2>
        <p className="opacity-90">Here's what's happening with your store today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                {renderChange(stat.change)}
                {stat.change === null && stat.subtitle && (
                  <p className="text-sm text-gray-400 mt-1">{stat.subtitle}</p>
                )}
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order status breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Orders by Status</h3>
            <p className="text-sm text-gray-500 mt-1">
              Breakdown of all {loadingStats ? '—' : formatNumber(totalOrders)} orders
            </p>
          </div>
          <Link to="/orders" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
            View all orders
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { key: 'placed', label: 'Placed', className: 'bg-gray-50 text-gray-800 border-gray-200' },
            { key: 'processing', label: 'Processing', className: 'bg-blue-50 text-blue-800 border-blue-200' },
            { key: 'shipped', label: 'Shipped', className: 'bg-purple-50 text-purple-800 border-purple-200' },
            { key: 'delivered', label: 'Delivered', className: 'bg-green-50 text-green-800 border-green-200' },
            { key: 'cancelled', label: 'Cancelled', className: 'bg-red-50 text-red-800 border-red-200' },
            { key: 'failed', label: 'Payment Failed', className: 'bg-amber-50 text-amber-800 border-amber-200' },
          ].map((item) => (
            <div
              key={item.key}
              className={`rounded-lg border p-4 ${item.className}`}
            >
              <p className="text-xs font-medium uppercase tracking-wide opacity-80">{item.label}</p>
              <p className="text-2xl font-bold mt-1">
                {loadingStats ? '—' : formatNumber(orderStatusCounts?.[item.key] ?? 0)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Collections */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Collections</h3>
          <p className="text-sm text-gray-600 mt-1">Latest collections added to your store</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collections.slice(0, 3).map((collection) => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/create-collection"
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors block"
          >
            <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Create Collection</p>
            <p className="text-xs text-gray-500">Add a new product collection</p>
          </Link>
          
          <Link 
            to="/create-product"
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors block"
          >
            <Shirt className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Add Product</p>
            <p className="text-xs text-gray-500">Add products to collections</p>
          </Link>
          
          <Link
            to="/analytics"
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors block text-center"
          >
            <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">View Analytics</p>
            <p className="text-xs text-gray-500">Check store performance</p>
          </Link>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Danger Zone</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-xl">
                Clears all store data (orders, customers, subscribers, coupons, analytics)
                while keeping your products and collections. This cannot be undone.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowResetModal(true)}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            Reset Store Data
          </button>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Reset Store Data
              </h3>
              <button onClick={closeResetModal} className="text-gray-400 hover:text-gray-600" disabled={resetting}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {resetResult ? (
                <div>
                  <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                    {resetResult.message || 'Data cleared successfully.'} Removed{' '}
                    <span className="font-semibold">{resetResult.total_deleted ?? 0}</span> records.
                  </div>
                  {resetResult.cleared && Object.keys(resetResult.cleared).length > 0 && (
                    <ul className="mt-3 text-sm text-gray-600 space-y-1">
                      {Object.entries(resetResult.cleared).map(([name, count]) => (
                        <li key={name} className="flex justify-between border-b border-gray-100 py-1">
                          <span className="capitalize">{name.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    onClick={closeResetModal}
                    className="mt-4 w-full px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    This will permanently delete <span className="font-semibold">orders, customers,
                    subscribers, coupons and analytics</span>. Your products and collections will be kept.
                  </p>
                  <p className="text-sm text-gray-600">
                    Type <span className="font-mono font-semibold text-red-600">RESET</span> to confirm.
                  </p>
                  <input
                    type="text"
                    value={resetConfirmText}
                    onChange={(e) => setResetConfirmText(e.target.value)}
                    placeholder="RESET"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    disabled={resetting}
                  />
                  {resetError && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                      {resetError}
                    </div>
                  )}
                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={closeResetModal}
                      disabled={resetting}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetData}
                      disabled={resetting || resetConfirmText.trim().toUpperCase() !== 'RESET'}
                      className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resetting ? 'Resetting...' : 'Confirm Reset'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 